/**
 * Navigation rail for the Investigator client.
 *
 * Visually matched to ReportGen's rail so switching between the two apps does
 * not feel like a jump: same 240/72 widths, the same OVERVIEW / ACCOUNT caption
 * sections, the same double-chevron collapse control in the header, and the
 * same ListItemButton treatment for nav items.
 *
 * What is NOT carried over is ReportGen's 776 lines of nav for ~15 routes that
 * do not exist here, along with the report job store, co-review hooks and
 * StationAdmin flag it reads to decide what to show. This app has one route.
 */
import React from "react";
import {
  Box,
  Button,
  Divider,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import FolderSharedOutlinedIcon from "@mui/icons-material/FolderSharedOutlined";
import LogoutIcon from "@mui/icons-material/Logout";
import DarkModeOutlinedIcon from "@mui/icons-material/DarkModeOutlined";
import LightModeOutlinedIcon from "@mui/icons-material/LightModeOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import OpenInNewOutlinedIcon from "@mui/icons-material/OpenInNewOutlined";
import KeyboardDoubleArrowLeftOutlinedIcon from "@mui/icons-material/KeyboardDoubleArrowLeftOutlined";
import KeyboardDoubleArrowRightOutlinedIcon from "@mui/icons-material/KeyboardDoubleArrowRightOutlined";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@workos-inc/authkit-react";
import { useColorMode } from "../../context/ColorModeContext";
import { useLayout } from "../../context/LayoutContext";
import { queryClient } from "../../shared/api/queryClient";
import { env } from "../../shared/config/env";
import { navRailWidth } from "./navRail";

/** Uppercase group label, as ReportGen's OVERVIEW/ACCOUNT headers. */
const SectionLabel: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <Typography
    variant="caption"
    sx={{
      fontWeight: 700,
      color: "text.primary",
      letterSpacing: 0.5,
      textTransform: "uppercase",
    }}
  >
    {children}
  </Typography>
);

export const SideBar: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { signOut } = useAuth();
  const { mode, toggleColorMode } = useColorMode();
  // The collapse state lives in LayoutContext, not here: TopAppBar sizes its
  // branding block to the rail, and ToastOffsetSync shifts toasts to stay
  // centred over the content. Local state left both of those reading a value
  // that never changed.
  const { sidebarOpen: expanded, setSidebarOpen } = useLayout();

  const width = navRailWidth(expanded);
  const isIncidents = location.pathname.startsWith("/incidents");
  const reportGenUrl = env.apps.reportGen;

  const handleSignOut = () => {
    // Session end must not leave another officer's cached case data behind.
    queryClient.clear();
    signOut({ returnTo: `${window.location.origin}/login` });
  };

  /** Nav/action row, styled as ReportGen's topMenuItems. */
  const item = (
    label: string,
    icon: React.ReactNode,
    selected: boolean,
    onClick: () => void,
  ) => (
    <Tooltip title={expanded ? "" : label} placement="right" arrow>
      <ListItemButton
        selected={selected}
        onClick={onClick}
        sx={{
          minHeight: 40,
          justifyContent: expanded ? "initial" : "center",
          mx: expanded ? 1 : "auto",
          width: expanded ? "auto" : 40,
          px: expanded ? 1.5 : 0,
          mb: 0.5,
          borderRadius: 2,
          color: selected ? "primary.main" : "text.primary",
          bgcolor: selected ? "action.selected" : "transparent",
          "&:hover": {
            bgcolor: selected ? "action.selected" : "action.hover",
            color: "text.primary",
          },
          "& .MuiListItemIcon-root": { color: "inherit" },
        }}
      >
        <ListItemIcon
          sx={{ minWidth: 0, mr: expanded ? 2 : 0, justifyContent: "center" }}
        >
          {icon}
        </ListItemIcon>
        {expanded && (
          <ListItemText
            primary={label}
            primaryTypographyProps={{
              fontSize: "0.875rem",
              fontWeight: selected ? 600 : 500,
              lineHeight: 1.2,
            }}
            sx={{ whiteSpace: "normal" }}
          />
        )}
      </ListItemButton>
    </Tooltip>
  );

  return (
    <Box
      component="nav"
      aria-label="Main navigation"
      sx={{
        width,
        flexShrink: 0,
        display: "flex",
        flexDirection: "column",
        borderRight: `1px solid ${theme.palette.divider}`,
        bgcolor: theme.palette.background.paper,
        transition: theme.transitions.create("width", {
          duration: theme.transitions.duration.shorter,
        }),
        overflowX: "hidden",
      }}
    >
      {/* Header: group label + the collapse control, as in ReportGen. */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: expanded ? "space-between" : "center",
          pl: expanded ? 2.5 : 0,
          pr: expanded ? 1 : 0,
          minHeight: 48,
        }}
      >
        {expanded && <SectionLabel>Overview</SectionLabel>}
        <IconButton
          size="small"
          onClick={(event) => {
            setSidebarOpen((open) => !open);
            // Drop focus so the ring does not linger on a button whose meaning
            // just inverted.
            event.currentTarget.blur();
          }}
          aria-label={expanded ? "Collapse navigation" : "Expand navigation"}
        >
          {expanded ? (
            <KeyboardDoubleArrowLeftOutlinedIcon />
          ) : (
            <KeyboardDoubleArrowRightOutlinedIcon />
          )}
        </IconButton>
      </Box>

      <List>
        {item("Incidents", <FolderSharedOutlinedIcon />, isIncidents, () =>
          navigate("/incidents"),
        )}
      </List>

      {/*
        Cross-app link, the mirror of the Investigator button in ReportGen's
        rail. It wears ReportGen's brand gradient rather than this app's navy
        and periwinkle, for the same reason that button wears navy: the button
        that leaves should look like where it goes.

        A real anchor, not an onClick, so cmd/middle click, "copy link" and the
        status-bar preview all behave. Hidden entirely when no origin is
        configured, rather than rendering a dead link.
      */}
      {reportGenUrl && (
        <Tooltip
          title={
            expanded ? "Opens in a new tab" : "ReportGen — opens in a new tab"
          }
          // When the label is visible, the tooltip DESCRIBES the button. Without
          // this MUI applies the title as aria-label, so the accessible name
          // becomes the tooltip text and stops matching the visible word --
          // which breaks "click ReportGen" voice control and reads oddly to a
          // screen reader. Collapsed there is no visible text, so the tooltip
          // has to be the name and describeChild goes off.
          describeChild={expanded}
          placement="right"
          arrow
        >
          <Button
            variant="contained"
            component="a"
            href={reportGenUrl}
            target="_blank"
            rel="noopener"
            startIcon={<DescriptionOutlinedIcon sx={{ fontSize: 20 }} />}
            endIcon={
              expanded ? (
                <OpenInNewOutlinedIcon sx={{ fontSize: 14, opacity: 0.8 }} />
              ) : undefined
            }
            sx={{
              mx: expanded ? 1 : "auto",
              mt: 0.5,
              mb: 1,
              width: expanded ? `calc(100% - ${theme.spacing(2)})` : 44,
              maxWidth: expanded ? `calc(100% - ${theme.spacing(2)})` : 44,
              minWidth: 0,
              height: 44,
              boxSizing: "border-box",
              overflow: "hidden",
              textTransform: "none",
              fontSize: "0.9rem",
              fontWeight: 600,
              borderRadius: 2,
              color: theme.palette.gradient.onBrand,
              background: theme.palette.gradient.brand,
              transition: "all 0.2s ease-in-out",
              "&:hover": {
                transform: "translateY(-1px)",
                background: theme.palette.gradient.brandHover,
              },
              "&:active": { transform: "translateY(0)" },
              "&:focus-visible": {
                outline: `2px solid ${theme.palette.primary.main}`,
                outlineOffset: 2,
              },
              "@media (prefers-reduced-motion: reduce)": {
                "&:hover": { transform: "none" },
              },
              "& .MuiButton-startIcon": { mr: expanded ? 1 : 0, ml: 0 },
              ...(!expanded && {
                p: 0,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                "& .MuiButton-startIcon": { m: 0 },
              }),
            }}
          >
            {expanded && "ReportGen"}
          </Button>
        </Tooltip>
      )}

      <Box sx={{ flexGrow: 1 }} />

      <Divider sx={{ borderColor: alpha(theme.palette.divider, 0.8) }} />

      <List>
        {item(
          mode === "dark" ? "Light Mode" : "Dark Mode",
          mode === "dark" ? <LightModeOutlinedIcon /> : <DarkModeOutlinedIcon />,
          false,
          toggleColorMode,
        )}
      </List>

      {expanded && (
        <Box sx={{ px: 2.5, pb: 0.5 }}>
          <SectionLabel>Account</SectionLabel>
        </Box>
      )}

      <List sx={{ pb: 1 }}>
        {item("Sign out", <LogoutIcon />, false, handleSignOut)}
      </List>
    </Box>
  );
};

export default SideBar;
